/**
 * PATH: packages/ui/src/components/adaptive-overlay/AdaptiveOverlay.tsx
 * FILE_ID: UI-ADAPTIVE-OVERLAY-001
 * PURPOSE: Shared portal overlay with popover and bottom-sheet geometry.
 * WHY: Menus must escape clipping ancestors and survive internal scrolling.
 */

import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import {
  popoverGeometryFromAnchor,
  type PopoverGeometry,
} from './popoverGeometry'

export type AdaptiveOverlayMode = 'popover' | 'sheet' | 'auto'

export interface AdaptiveOverlayProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  anchorRef?: RefObject<HTMLElement | null>
  mode?: AdaptiveOverlayMode
  autoSheetBelowPx?: number
  label: string
  children: ReactNode
  className?: string
  testId?: string
  popoverWidth?: number
  /** Hint for flip-above math before the panel has painted. */
  heightHint?: number
}

export function AdaptiveOverlay({
  open,
  onOpenChange,
  anchorRef,
  mode = 'auto',
  autoSheetBelowPx = 640,
  label,
  children,
  className = '',
  testId,
  popoverWidth = 352,
  heightHint = 200,
}: AdaptiveOverlayProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [geometry, setGeometry] = useState<PopoverGeometry | null>(null)
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? autoSheetBelowPx : window.innerWidth,
  )
  const useSheet =
    mode === 'sheet'
    || (mode === 'auto' && viewportWidth < autoSheetBelowPx)

  useEffect(() => {
    if (!open) return
    const previouslyFocused = document.activeElement as HTMLElement | null
    const update = () => {
      setViewportWidth(window.innerWidth)
      if (!useSheet) {
        const painted = panelRef.current?.offsetHeight
        setGeometry(
          popoverGeometryFromAnchor(
            anchorRef?.current ?? null,
            popoverWidth,
            painted && painted > 0 ? painted : heightHint,
          ),
        )
      }
    }
    update()
    // Re-measure after paint so flip-above uses the real panel height.
    const raf = window.requestAnimationFrame(update)
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node
      if (
        !panelRef.current?.contains(target)
        && !anchorRef?.current?.contains(target)
      ) {
        onOpenChange(false)
      }
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false)
        return
      }
      if (event.key !== 'Tab' || !panelRef.current) return
      const focusable = [
        ...panelRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ),
      ]
      if (focusable.length === 0) {
        event.preventDefault()
        panelRef.current.focus()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    queueMicrotask(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(
        '[autofocus], input:not([disabled]), button:not([disabled]), select:not([disabled]), [href]',
      )
      ;(first ?? panelRef.current)?.focus()
    })
    document.addEventListener('pointerdown', onPointer, true)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', update)
    // Capture phase: the thing that scrolls is usually an ancestor div (the QA
    // areas rail), and scroll does not bubble, so a normal listener never runs
    // and the fixed panel is left behind where the anchor used to be.
    window.addEventListener('scroll', update, true)
    return () => {
      window.cancelAnimationFrame(raf)
      document.removeEventListener('pointerdown', onPointer, true)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
      previouslyFocused?.focus()
    }
  }, [
    anchorRef,
    heightHint,
    onOpenChange,
    open,
    popoverWidth,
    useSheet,
  ])

  if (!open || typeof document === 'undefined') return null
  if (!useSheet && !geometry) return null

  const style: CSSProperties | undefined = useSheet
    ? undefined
    : geometry
      ? {
          left: geometry.left,
          top: geometry.top,
          width: geometry.width,
          maxHeight: geometry.maxHeight,
        }
      : undefined
  // Sheet panels must be in-flow (`relative`), not `fixed`. The backdrop uses
  // `backdrop-filter`, which makes a containing block for fixed descendants;
  // a fixed sheet then paints at the wrong layer / with zero visible chrome
  // (Jobs list mobile view switcher: blur with no options — 2026-08-28).
  // MobileActionSheet uses the same relative-inside-items-end pattern.
  const panel = (
    <div
      ref={panelRef}
      role="dialog"
      tabIndex={-1}
      aria-modal={useSheet || undefined}
      aria-label={label}
      className={[
        'overflow-y-auto overscroll-contain border border-border',
        'bg-popover text-popover-foreground shadow-2xl',
        useSheet
          ? [
              'relative z-10 w-full max-h-[min(78vh,calc(100dvh-var(--mobile-dock-h,0px)-0.5rem))]',
              'rounded-t-2xl px-3 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2',
            ].join(' ')
          : 'fixed z-[1200] rounded-xl p-2',
        className,
      ].join(' ')}
      style={style}
      data-adaptive-overlay={useSheet ? 'sheet' : 'popover'}
      data-adaptive-side={useSheet ? undefined : geometry?.side}
      data-testid={testId}
    >
      {children}
    </div>
  )

  return createPortal(
    useSheet ? (
      <div
        className="fixed inset-0 z-[1300] flex items-end bg-black/45 backdrop-blur-[1px]"
        role="presentation"
        data-adaptive-overlay-root="sheet"
      >
        {panel}
      </div>
    ) : (
      panel
    ),
    document.body,
  )
}
